import React, { useEffect } from 'react';
import { Form, Input, Switch, Button } from 'antd';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { ICalendarBookingFormContentProps } from '@/interfaces/ICalendarWorkflows';

const CalendarBookingFormContent: React.FC<ICalendarBookingFormContentProps> = ({
	form,
	isEditing,
	currentWorkflow,
	onSubmit,
	onCancel,
	mutation,
	debrand = false,
	isNoviUser = false,
	getDefaultFormValues,
	getEditFormValues,
}) => {
	useEffect(() => {
		if (currentWorkflow) {
			setTimeout(() => form.setFieldsValue(getEditFormValues(currentWorkflow)), 100);
		} else {
			setTimeout(() => form.setFieldsValue(getDefaultFormValues()), 100);
		}
	}, [currentWorkflow, form, getDefaultFormValues, getEditFormValues]);

	const handleFormSubmit = async (values: any) => {
		await onSubmit(values, isEditing, currentWorkflow);
	};

	return (
		<Form form={form} layout="vertical" onFinish={handleFormSubmit} className="mt-4">
			<div className="border-b pb-4 mb-4">
				<LabelWithDescription
					label="Enable Short URLs"
					description="Enable short URLs to keep track of the links and get analytics."
				/>
				<Form.Item name="shortURLEnabled" valuePropName="checked" className="mt-2">
					<Switch defaultChecked />
				</Form.Item>
			</div>

			<div className="border-b pb-4 mb-4">
				<LabelWithDescription
					label="Scenario Description"
					description="Describe when you want to send the calendar booking link. The AI will detect when this scenario applies."
				/>
				<Form.Item
					name="description"
					className="mt-2"
					rules={[
						{
							validator: (_, value) => {
								if (!value || value.trim() === '') {
									return Promise.reject("Description can't be blank");
								}
								return Promise.resolve();
							},
						},
					]}
				>
					<Input.TextArea
						placeholder="e.g. Send a booking link when caller wants to schedule an appointment"
						autoSize={{ minRows: 2, maxRows: 4 }}
					/>
				</Form.Item>
			</div>

			<div className="border-b pb-4 mb-4">
				<LabelWithDescription
					label="Text Message"
					description="The text message that will be sent to the caller with your booking link."
				/>
				<Form.Item
					name="textMessage"
					className="mt-2"
					rules={[
						{
							validator: (_, value) => {
								if (!value || value.trim() === '') {
									return Promise.reject("Text Message can't be blank");
								}
								return Promise.resolve();
							},
						},
						{ max: 1000, message: 'Max 1000 characters allowed' },
					]}
				>
					<Input.TextArea
						placeholder="e.g. Hi! Here's the link to book your appointment: [YOUR_BOOKING_LINK]"
						autoSize={{ minRows: 3, maxRows: 6 }}
					/>
				</Form.Item>
				<div className="text-blue-700 text-xs font-semibold">
					* Max 1000 characters allowed. Replace [YOUR_BOOKING_LINK] with your actual booking URL.
				</div>
			</div>

			<div className="border-b pb-4 mb-4">
				<LabelWithDescription
					label="What should the AI do next?"
					description="Instructions for the AI to follow after sending the booking link."
				/>
				<Form.Item name="postExecutionPrompt" className="mt-2">
					<Input.TextArea
						placeholder="e.g. Tell the caller you've sent them a booking link via text message..."
						autoSize={{ minRows: 2, maxRows: 4 }}
					/>
				</Form.Item>
			</div>

			{(debrand || isNoviUser) && (
				<div className="border-b pb-4 mb-4">
					<LabelWithDescription
						label="Custom Text Footer"
						description="Add a custom footer to your text message."
					/>
					<Form.Item
						name="customTextFooter"
						className="mt-2"
						rules={[{ max: 100, message: 'Max 100 characters allowed' }]}
					>
						<Input.TextArea
							placeholder="e.g. Sent by AI Receptionist"
							autoSize={{ minRows: 1, maxRows: 2 }}
						/>
					</Form.Item>
					<div className="text-blue-700 text-xs font-semibold">* Max 100 characters allowed</div>
				</div>
			)}

			<div className="pb-4 mb-4">
				<LabelWithDescription
					label="Ask before sending Text SMS"
					description="If enabled, the AI will ask for permission before sending the text message."
				/>
				<Form.Item name="askForPermissionOnText" valuePropName="checked" className="mt-2">
					<Switch />
				</Form.Item>
			</div>

			<div className="flex justify-end gap-2 pt-4 border-t">
				<Button onClick={onCancel}>Cancel</Button>
				<Button type="primary" htmlType="submit" loading={mutation?.isPending}>
					{isEditing ? 'Update Booking Workflow' : 'Add Booking Workflow'}
				</Button>
			</div>
		</Form>
	);
};

export default CalendarBookingFormContent;
